import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { FilterField } from '../../../api/models/filters.model';
import { NO_RESULTS_FOUND, SEARCHING, START_TYPING_TO_SEARCH } from '../../../constants/messages';

interface SearchableFilterFieldProps {
	field: FilterField;
	value: string[];
	onChange: (value: string[]) => void;
	searchResults: string[];
	loading: boolean;
	inputValue: string;
	onInputChange: (val: string) => void;
}

function SearchableFilterField(props: SearchableFilterFieldProps) {
	const { field, value, onChange, searchResults, loading, inputValue, onInputChange } = props;

	// Combine selected values with search results to ensure selected items remain visible
	const options = useMemo(() => {
		const combined = new Set([...value, ...searchResults]);
		return Array.from(combined);
	}, [value, searchResults]);

	const getNoOptionsText = () => {
		if (inputValue.length < 3) {
			return START_TYPING_TO_SEARCH;
		}
		if (loading) {
			return SEARCHING;
		}
		return NO_RESULTS_FOUND;
	};

	return (
		<Box>
			<Autocomplete
				multiple
				id={`searchable-filter-${field.name}`}
				options={options}
				value={value}
				inputValue={inputValue}
				onInputChange={(_event, newInputValue, reason) => {
					if (reason === 'input') {
						onInputChange(newInputValue);
					}
				}}
				onChange={(_event, newValue) => {
					onChange(newValue);
				}}
				loading={loading}
				noOptionsText={getNoOptionsText()}
				filterOptions={(x) => x} // Disable client-side filtering since we filter server-side
				renderInput={(params) => (
					<TextField
						{...params}
						variant="outlined"
						size="small"
						placeholder={value.length === 0 ? START_TYPING_TO_SEARCH : ''}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				renderOption={(props, option) => (
					<Box component="li" {...props} key={option}>
						<Typography variant="body2">{option}</Typography>
					</Box>
				)}
				isOptionEqualToValue={(option, value) => option === value}
				data-testid={`searchable-filter-field-${field.name}`}
			/>
		</Box>
	);
}

export default SearchableFilterField;
